import { useCallback, useRef, useState } from "react";
import { DriverSection } from "../../redux/onBoardingSlice.types";
import { resolveReason } from "../utils";
import { SECTION_STATUS } from "./constants";
import { SectionTrackingState } from "./types";

/**
 * Number of fields in a section that are eligible for rejection: has a key, and is
 * either a text field or an image field with a value — mirrors the `selectableFieldKeys`
 * predicate in index.tsx so the denominator only counts fields the UI actually lets
 * the reviewer select/reject.
 */
const rejectableFieldCount = (section?: DriverSection) =>
  section?.fields.filter((f) => f.key && (f.type === "text" || (f.type === "image" && f.value))).length ?? 0;

/** Derives section status from how many of its rejectable fields are currently rejected. */
const deriveStatus = (
  rejectedCount: number,
  totalCount: number,
  fallback: SectionTrackingState["status"]
): SectionTrackingState["status"] => {
  if (rejectedCount === 0) return fallback;
  if (totalCount > 0 && rejectedCount >= totalCount) return SECTION_STATUS.REJECTED;
  return SECTION_STATUS.PARTIALLY_REJECTED;
};

/**
 * Manages section review state (viewed / rejected / picker open) and all
 * related event handlers.
 *
 * Rejection works in per-field "edit mode": clicking Reject opens checkboxes
 * on every field/image in the section plus a reason picker; confirming stamps
 * only the checked fields with the chosen reason. A section can be rejected
 * across multiple rounds with different reasons for different fields.
 *
 * Isolated from rendering so it can be tested with `renderHook` without
 * mounting any UI.
 */
export const useSectionStates = (sortedSections: DriverSection[]) => {
  const [sectionStates, setSectionStates] = useState<Record<string, SectionTrackingState>>({});
  const sectionPickerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const init = useCallback((sections: DriverSection[]) => {
    const initial: Record<string, SectionTrackingState> = {};
    sections.forEach((s) => {
      initial[s.sectionId] = {
        status: SECTION_STATUS.UNVIEWED,
        pendingReason: "",
        pendingComment: "",
        selectedFieldKeys: [],
        rejectedFields: {},
        showRejectPicker: false,
      };
    });
    setSectionStates(initial);
  }, []);

  const markViewed = useCallback((sectionId: string) => {
    setSectionStates((prev) => {
      if (prev[sectionId]?.status !== SECTION_STATUS.UNVIEWED) return prev;
      return { ...prev, [sectionId]: { ...prev[sectionId], status: SECTION_STATUS.VIEWED } };
    });
  }, []);

  const handleReject = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], showRejectPicker: true, selectedFieldKeys: [] },
    }));
    setTimeout(() => {
      sectionPickerRefs.current[sectionId]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const handleToggleField = useCallback((sectionId: string, fieldKey: string) => {
    setSectionStates((prev) => {
      const s = prev[sectionId];
      if (!s) return prev;
      const isSelected = s.selectedFieldKeys.includes(fieldKey);
      const selectedFieldKeys = isSelected
        ? s.selectedFieldKeys.filter((k) => k !== fieldKey)
        : [...s.selectedFieldKeys, fieldKey];
      return { ...prev, [sectionId]: { ...s, selectedFieldKeys } };
    });
  }, []);

  /** Selects every given key, or clears the selection if all of them are already selected. */
  const handleSelectAllFields = useCallback((sectionId: string, fieldKeys: string[]) => {
    setSectionStates((prev) => {
      const s = prev[sectionId];
      if (!s) return prev;
      const allSelected =
        fieldKeys.length > 0 && fieldKeys.every((k) => s.selectedFieldKeys.includes(k));
      const selectedFieldKeys = allSelected ? [] : fieldKeys;
      return { ...prev, [sectionId]: { ...s, selectedFieldKeys } };
    });
  }, []);

  const handleRejectConfirm = useCallback(
    (sectionId: string) => {
      setSectionStates((prev) => {
        const s = prev[sectionId];
        if (!s?.pendingReason || s.selectedFieldKeys.length === 0) return prev;
        const section = sortedSections.find((sec) => sec.sectionId === sectionId);
        const reasons = section?.rejectionReasons ?? [];
        const resolvedReason = resolveReason(s.pendingReason, s.pendingComment, reasons);
        const rejectedFields = { ...s.rejectedFields };
        s.selectedFieldKeys.forEach((key) => {
          rejectedFields[key] = resolvedReason;
        });
        return {
          ...prev,
          [sectionId]: {
            ...s,
            status: deriveStatus(Object.keys(rejectedFields).length, rejectableFieldCount(section), s.status),
            rejectedFields,
            pendingReason: "",
            pendingComment: "",
            selectedFieldKeys: [],
            showRejectPicker: false,
          },
        };
      });
    },
    [sortedSections]
  );

  const handleRejectCancel = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        pendingReason: "",
        pendingComment: "",
        selectedFieldKeys: [],
        showRejectPicker: false,
      },
    }));
  }, []);

  const handleSelectReason = useCallback((sectionId: string, reason: string) => {
    setSectionStates((prev) => {
      const s = prev[sectionId];
      if (!s) return prev;
      return { ...prev, [sectionId]: { ...s, pendingReason: reason, pendingComment: "" } };
    });
  }, []);

  const handleCommentChange = useCallback((sectionId: string, value: string) => {
    setSectionStates((prev) => {
      const s = prev[sectionId];
      if (!s) return prev;
      return { ...prev, [sectionId]: { ...s, pendingComment: value } };
    });
  }, []);

  const handleClearReason = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], pendingReason: "", pendingComment: "" },
    }));
  }, []);

  /** Undoes a single rejected field when `fieldKey` is given, otherwise clears the whole section. */
  const handleUnreject = useCallback(
    (sectionId: string, fieldKey?: string) => {
      setSectionStates((prev) => {
        const s = prev[sectionId];
        if (!s) return prev;
        const section = sortedSections.find((sec) => sec.sectionId === sectionId);
        const rejectedFields = fieldKey ? { ...s.rejectedFields } : {};
        if (fieldKey) delete rejectedFields[fieldKey];
        return {
          ...prev,
          [sectionId]: {
            ...s,
            status: deriveStatus(
              Object.keys(rejectedFields).length,
              rejectableFieldCount(section),
              SECTION_STATUS.VIEWED
            ),
            rejectedFields,
            showRejectPicker: false,
          },
        };
      });
    },
    [sortedSections]
  );

  return {
    sectionStates,
    sectionPickerRefs,
    init,
    markViewed,
    handleReject,
    handleToggleField,
    handleSelectAllFields,
    handleRejectConfirm,
    handleRejectCancel,
    handleSelectReason,
    handleCommentChange,
    handleClearReason,
    handleUnreject,
  };
};
